import React, {
  type ComponentPropsWithoutRef,
  type ComponentType,
  forwardRef,
  type ForwardRefExoticComponent,
  type RefAttributes,
  type SVGProps,
} from 'react';

import { Styled } from './styles';
import type { IconIntent, IconSize } from './types';

export interface IconComponentProps extends ComponentPropsWithoutRef<'i'> {
  intent?: IconIntent;
  size?: IconSize;
  tooltip?: string;
  'data-testid'?: string;
}

export type IconComponent = ForwardRefExoticComponent<IconComponentProps & RefAttributes<HTMLElement>>;

function toKebabCase(name: string): string {
  return name
    .replace(/Icon$/, '')
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/([A-Z])([A-Z][a-z])/g, '$1-$2')
    .toLowerCase();
}

export function createIcon(SvgComponent: ComponentType<SVGProps<SVGSVGElement>>, displayName: string): IconComponent {
  const defaultTestId = toKebabCase(displayName);
  const IconComp = forwardRef<HTMLElement, IconComponentProps>(
    ({ intent, tooltip, size, 'data-testid': testId, ...props }, ref) => (
      <Styled.Container
        {...props}
        ref={ref}
        title={tooltip}
        size={size}
        intent={intent}
        data-testid={testId ?? defaultTestId}
      >
        <SvgComponent />
      </Styled.Container>
    ),
  );
  IconComp.displayName = displayName;
  return IconComp;
}
